import React, { useState, useRef, useEffect } from "react";
// import InviteCard from "./InviteCard";
import EventCard from "./EventCard";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import InviteModal from "./InviteModal";

const MeetingsSection = () => {
	const events = useSelector(state => state.events.events);
	const [activeTab, setActiveTab] = useState("all");
	// const [showFadeEffect, setShowFadeEffect] = useState(false);
	const [isSmallScreen, setIsSmallScreen] = useState(false);
	const gridRef = useRef(null);

	const now = DateTime.now();
	const filteredEvents = events
		.filter(event => DateTime.fromSeconds(event.start?.timestamp || 0) > now)
		.sort((a, b) => (a.start?.timestamp || 0) - (b.start?.timestamp || 0));

	// Check for small screen
	useEffect(() => {
		const checkScreenSize = () => {
			setIsSmallScreen(window.innerWidth <= 1000);
		};

		checkScreenSize();
		window.addEventListener("resize", checkScreenSize);

		return () => window.removeEventListener("resize", checkScreenSize);
	}, []);

	// Filter events based on active tab
	const getFilteredEventsByTab = () => {
		const startOfDay = now.startOf("day");
		const endOfDay = now.endOf("day");
		const startOfWeek = now.startOf("week");
		const endOfWeek = now.endOf("week");
		const startOfMonth = now.startOf("month");
		const endOfMonth = now.endOf("month");

		switch (activeTab) {
			case "today":
				return filteredEvents.filter(event => {
					const eventTime = DateTime.fromSeconds(event.start?.timestamp || 0);
					return eventTime >= startOfDay && eventTime <= endOfDay;
				});
			case "week":
				return filteredEvents.filter(event => {
					const eventTime = DateTime.fromSeconds(event.start?.timestamp || 0);
					return eventTime >= startOfWeek && eventTime <= endOfWeek;
				});
			case "month":
				return filteredEvents.filter(event => {
					const eventTime = DateTime.fromSeconds(event.start?.timestamp || 0);
					return eventTime >= startOfMonth && eventTime <= endOfMonth;
				});
			default:
				return filteredEvents;
		}
	};

	const displayEvents = getFilteredEventsByTab();
	const todayCount = filteredEvents.filter(event => {
		const eventTime = DateTime.fromSeconds(event.start?.timestamp || 0);
		return eventTime >= now.startOf("day") && eventTime <= now.endOf("day");
	}).length;

	const weekCount = filteredEvents.filter(event => {
		const eventTime = DateTime.fromSeconds(event.start?.timestamp || 0);
		return eventTime >= now.startOf("week") && eventTime <= now.endOf("week");
	}).length;

	const monthCount = filteredEvents.filter(event => {
		const eventTime = DateTime.fromSeconds(event.start?.timestamp || 0);
		return eventTime >= now.startOf("month") && eventTime <= now.endOf("month");
	}).length;

	// Dynamic tab labels based on screen size
	const tabs = [
		{
			key: "all",
			label: isSmallScreen ? `All (${filteredEvents.length})` : `All (${filteredEvents.length})`,
			shortLabel: `All (${filteredEvents.length})`,
		},
		{
			key: "today",
			label: isSmallScreen ? `Today (${todayCount})` : `Today (${todayCount})`,
			shortLabel: `Today (${todayCount})`,
		},
		{
			key: "week",
			label: isSmallScreen ? `Week (${weekCount})` : `This week (${weekCount})`,
			shortLabel: `Week (${weekCount})`,
		},
		{
			key: "month",
			label: isSmallScreen ? `Month (${monthCount})` : `This month (${monthCount})`,
			shortLabel: `Month (${monthCount})`,
		},
	];

	// Check for overflow when events or tab changes
	useEffect(() => {
		const checkOverflow = () => {
			if (gridRef.current) {
				// const hasOverflow = gridRef.current.scrollWidth > gridRef.current.clientWidth;
				// setShowFadeEffect(hasOverflow);
			}
		};

		// Check immediately
		checkOverflow();

		// Check after a short delay to ensure content is rendered
		const timer = setTimeout(checkOverflow, 100);

		// Add resize listener
		window.addEventListener("resize", checkOverflow);

		return () => {
			clearTimeout(timer);
			window.removeEventListener("resize", checkOverflow);
		};
	}, [displayEvents, activeTab]);

	// Get responsive styles
	const getResponsiveStyles = () => {
		if (isSmallScreen) {
			return {
				...styles,
				tabsContainer: {
					...styles.tabsContainer,
					gap: "1rem", // Reduced from 2rem
				},
				tab: {
					...styles.tab,
					fontSize: "11px", // Reduced from 12px
					padding: "0.6rem 0", // Slightly reduced padding
				},
			};
		}
		return styles;
	};

	const responsiveStyles = getResponsiveStyles();

	return (
		<div style={responsiveStyles.container}>
			{/* Invite Card and Meetings Section - Side by side */}
			<div style={responsiveStyles.mainLayout}>
				{/* Invite Card - Fixed position with top margin to align with cards */}
				<div style={responsiveStyles.inviteCardContainer}>
					<InviteModal />
				</div>

				{/* Meetings Section - Tabs and Cards */}
				<div style={responsiveStyles.meetingsContainer}>
					{/* Enhanced Tabs - Aligned with meeting cards */}
					<div style={responsiveStyles.tabsContainer}>
						{tabs.map(tab => (
							<button
								key={tab.key}
								style={{
									...responsiveStyles.tab,
									...(activeTab === tab.key ? responsiveStyles.activeTab : responsiveStyles.inactiveTab),
								}}
								onClick={() => setActiveTab(tab.key)}>
								{isSmallScreen ? tab.shortLabel : tab.label}
							</button>
						))}
					</div>

					{/* Meetings Grid - Scrollable */}
					<div ref={gridRef} style={responsiveStyles.meetingsGrid}>
						{displayEvents.map(event => (
							<EventCard key={event.id} event={event} />
						))}
					</div>

					{/* White fade effect overlay - only show when there's overflow */}
					{/* {showFadeEffect && <div style={responsiveStyles.meetingsGridFade}></div>} */}
				</div>
			</div>
		</div>
	);
};

const styles = {
	container: {
		width: "100%",
	},

	mainLayout: {
		display: "flex",
		gap: "1.5rem",
		alignItems: "flex-start",
	},

	inviteCardContainer: {
		marginTop: "5rem", // Align with cards after tabs
		flexShrink: 0, // Prevent shrinking
	},

	meetingsContainer: {
		flex: 1,
		minWidth: 0, // Important: allows flex child to shrink below content size
		position: "relative", // For the fade effect positioning
	},

	tabsContainer: {
		display: "flex",
		gap: "2rem",
		marginBottom: "2rem",
		flexWrap: "wrap",
	},

	meetingsGrid: {
		display: "flex",
		gap: "1.5rem",
		overflowX: "auto", // Changed from "scroll" to "auto"
		overflowY: "hidden", // Prevent vertical scroll
		paddingBottom: "10px",
		width: "100%", // Ensure full width
		// Add scrollbar styling
		scrollbarWidth: "thin", // Firefox
		scrollbarColor: "rgba(255, 255, 255, 0.3) transparent", // Firefox
		// Webkit browsers
		"::-webkit-scrollbar": {
			height: "6px",
		},
		"::-webkit-scrollbar-track": {
			background: "rgba(255, 255, 255, 0.1)",
			borderRadius: "3px",
		},
		"::-webkit-scrollbar-thumb": {
			background: "rgba(255, 255, 255, 0.3)",
			borderRadius: "3px",
		},
		"::-webkit-scrollbar-thumb:hover": {
			background: "rgba(255, 255, 255, 0.5)",
		},
	},

	// Add fade effect for meetings grid - positioned relative to meetings container
	meetingsGridFade: {
		position: "absolute",
		top: "4.6rem", // Start after the tabs (adjust based on tab height)
		right: 0,
		height: "210px", // Match event card height
		width: "50px", // Reduced from 150px to 80px
		background: "linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.5))",
		pointerEvents: "none",
		zIndex: 1,
		borderRadius: "0 16px 16px 0", // Rounded corners on the right side
	},

	tab: {
		padding: "0.75rem 0",
		fontSize: "12px",
		cursor: "pointer",
		transition: "all 0.3s ease",
		border: "none",
		background: "transparent",
		fontFamily: "inherit",
		fontWeight: "600",
		minWidth: "auto",
		whiteSpace: "nowrap",
		position: "relative",
		borderBottom: "2px solid transparent",
	},

	activeTab: {
		color: "white",
		fontWeight: "700",
		borderBottom: "2px solid white",
	},

	inactiveTab: {
		color: "rgba(255, 255, 255, 0.7)",
		fontWeight: "600",
		borderBottom: "2px solid transparent",
	},

	"@media (max-width: 768px)": {
		tabsContainer: {
			gap: "1rem",
		},
		tab: {
			padding: "0.6rem 0",
			fontSize: "0.9rem",
		},
		mainLayout: {
			flexDirection: "column",
		},
		inviteCardContainer: {
			marginTop: "0",
			alignSelf: "flex-start",
		},
	},
};

export default MeetingsSection;
